import React, { Ref, useEffect, useMemo, useRef, useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import ButtonLinkList from '@app/component/button-link-list/ButtonLinkList';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import ModalHandler from '@app/component/overlays/modal/ModalHandler';
import _isEmpty from 'lodash/isEmpty';
import Utilities from '@util/Utilities';
import AddHotelRoomTypeModal from '@app/site/secured/rateshopper/rates/settings/page/room-mapping/modal/AddHotelRoomTypeModal';
import EditHotelRoomTypeModal from '@app/site/secured/rateshopper/rates/settings/page/room-mapping/modal/EditHotelRoomTypeModal';
import Message from '@app/component/message/Message';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import DataProvider from '@app/model/_common/DataProvider';
import CompanyHotelParsingSettingState from '@app/model/company_hotel/CompanyHotelParsingSettingState';
import CompanyHotelRoomMappingItemFragment
	from '@app/site/secured/rateshopper/rates/settings/page/room-mapping/fragment/CompanyHotelRoomMappingItemFragment';
import { findProviderData } from '@app/component/provider/FindProviderData';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import {
	useAutoUpdateCompanyHotelRoomMappingTypesMutation,
	useEnrichHotelRoomsMutation,
	useSanitizeRoomMappingsMutation,
} from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import HotelWithRoomMappings from '@app/model/company_hotel/HotelWithRoomMappings';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import FormButton from '@app/component/form/button/FormButton';
import ProcessRequestExecutionState from '@app/model/process-request/ProcessRequestExecutionState';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import ProcessRequestQueryKeys from '@app/model/process-request/service/ProcessRequestQueryKeys';
import ProcessRequestService from '@app/model/process-request/service/ProcessRequestService';
import { CompanyHotelQueryKeys } from '@app/model/company_hotel/service/CompanyHotelQueryKeys';

const RateshopperRoomMappingSettingsPage = React.memo(() => {
	const { companyHotel } = useSelectedCompanyHotel();
	const addRoomTypeModal = useRef<ModalHandler>(null);
	const editRoomTypeModal = useRef<ModalHandler>(null);
	const [provider, setProvider] = useState(DataProvider.BOOKING);
	const toaster = useToaster();


	if (!!companyHotel) {

		const activeProviders = companyHotel.parsingSettings!.filter(c => c.state === CompanyHotelParsingSettingState.ACTIVE && c.variables.provider !== DataProvider.AIRBNB);
		const uniqueProviderValues = new Set(activeProviders.map(c => c.variables.provider));

		const providerItems = Array.from(uniqueProviderValues).map(provider => ({
			name: findProviderData(provider).name,
			onClick: () => setProvider(provider),
			icon: findProviderData(provider).icon,
		})).sort((a, b) => a.name.localeCompare(b.name));

		const toggleSystemRoomTypesVisibility = () => {
			const hasMappedSystemRoomTypes = companyHotel.getAllHotelsWithMappings().some(hwm => {
				return hwm.roomMappings.some(rm => {
					return rm.roomMappingType === HotelRoomMappingType.SINGLE
						|| rm.roomMappingType === HotelRoomMappingType.STANDARD
						|| rm.roomMappingType === HotelRoomMappingType.PREMIUM
						|| rm.roomMappingType === HotelRoomMappingType.SUITE;
				});
			});
			if (hasMappedSystemRoomTypes) {
				toaster.error('Room Mapping', 'You have mapped system room types, please unmap them first!');
			} else {
				/*saveSettingsMutation.mutateAsync({
					companyHotelId: companyHotel.id,
					input: getCompanyHotelSettingsInput({
						...companyHotel.settings,
						systemRoomTypesVisibility: !companyHotel.settings.systemRoomTypesVisibility,
					}),
				}).then(() => {
					toaster.success('Room Mapping', 'System room types visibility toggled successfully!');
				});*/
			}
		};

		const actions = useMemo(() => {
			const actions = [
				{
					name: 'Add a room type', onClick: () => addRoomTypeModal.current!.open(),
				},
				{
					name: 'Edit a room type', onClick: () => editRoomTypeModal.current!.open(),
				},
				{
					name: `${companyHotel.settings.roomMapping.showSystemRoomMappingTypes ? 'Hide' : 'Show'} system room types`,
					onClick: toggleSystemRoomTypesVisibility,
				},
			];
			return actions;
		}, [companyHotel]);

		return <main>
			<div className="bg-white p-4 sm:p-10 rounded-xl">
				<div className="pb-3 flex flex-wrap items-center justify-between border-b border-gray-200">
					<div className="mr-0 sm:mr-4 mb-4 lg:mb-0">
						<h2 className="text-base font-semibold leading-5 text-gray-900">
							Map yours & your competitors’ room types
						</h2>
						<p className="text-sm text-gray-500">
							Please assign each room type with our predefined categories, this will help you compare
							apples with apples.
						</p>
					</div>
					<div className="flex flex-wrap gap-x-3 gap-y-2 items-center">
						<ButtonLinkList state="white" description="Please select the source you wish to compare."
														items={providerItems}>
							<div className="flex items-center gap-x-2">
								{findProviderData(provider).icon}
								<span>{Utilities.snakeCaseToWords(provider)}</span>
							</div>
						</ButtonLinkList>
						<ButtonLinkList state="white" menuItemClassName="block px-4 py-1 text-sm cursor-pointer" items={actions}>Room
							Actions</ButtonLinkList>
					</div>

				</div>
				<CompanyHotelProviderRoomMappings companyHotel={companyHotel} provider={provider} />
				<HotelRoomTypeConfigurationModals companyHotel={companyHotel}
																					addRoomTypeModal={addRoomTypeModal}
																					editRoomTypeModal={editRoomTypeModal} />
			</div>
		</main>;
	}

	return <Message state="error">There was an error fetching the information. Please try again later!</Message>;

});

const CompanyHotelProviderRoomMappings: React.FunctionComponent<{
	companyHotel: CompanyHotel
	provider: DataProvider
}> = ({ companyHotel, provider }) => {

	const [selectedHotel, setSelectedHotel] = useState<HotelWithRoomMappings>(companyHotel.mainHotel.competitors[0]);
	const [hotels, setHotels] = useState<HotelWithRoomMappings[]>([]);
	const [activeEnrichment, setActiveEnrichment] = useState<{
		hotelId: number,
		hotelName: string,
		executions: ProcessRequestExecutionState[],
	} | null>(null);
	const dialog = useDialogue();
	const toaster = useToaster();
	const queryClient = useQueryClient();
	const automapHotelRoomsMutation = useAutoUpdateCompanyHotelRoomMappingTypesMutation({
		onSuccess: () => toaster.success('Room Mapping', 'Auto mapping completed successfully!'),
		onError: () => toaster.error('Room Mapping', 'Auto mapping could not be completed. Please try again later!'),
	});

	const automapping = () => {
		automapHotelRoomsMutation.mutate({
			companyHotelId: companyHotel.id,
			hotelId: selectedHotel.hotel.id,
		});
	};

	const enrichmentQueries = useQueries({
		queries: (activeEnrichment?.executions ?? []).map(execution => ({
			queryKey: ProcessRequestQueryKeys.executionState(execution.requestId),
			queryFn: () => ProcessRequestService.findProcessRequestExecutionState(execution.requestId),
			initialData: execution,
			refetchInterval: (query: { state: { data?: ProcessRequestExecutionState } }) =>
				query.state.data?.isFinished ? false : 1000,
			refetchOnWindowFocus: true,
		})),
	});

	const enrichHotelRoomsMutation = useEnrichHotelRoomsMutation({
		onSuccess: (executions, variables) => {
			const hotelName = companyHotel.findHotelWithMappingsById(variables.hotelId)?.hotel.name ?? 'selected hotel';
			setActiveEnrichment({ hotelId: variables.hotelId, hotelName, executions });
			toaster.info('Room enrichment', `Room enrichment started for ${hotelName}.`);
		},
		onError: () => toaster.error('Room enrichment', 'Room enrichment could not be started. Please try again later.'),
	});

	useEffect(() => {
		if (!activeEnrichment) return;

		if (enrichmentQueries.some(query => query.isError)) {
			toaster.error('Room enrichment', `Could not monitor room enrichment for ${activeEnrichment.hotelName}.`);
			setActiveEnrichment(null);
			return;
		}

		if (enrichmentQueries.length !== activeEnrichment.executions.length
			|| enrichmentQueries.some(query => query.data?.isFinished !== true)) return;

		const enrichmentFailed = enrichmentQueries.some(query => query.data?.isSuccessful !== true);
		setActiveEnrichment(null);
		void queryClient.invalidateQueries({ queryKey: CompanyHotelQueryKeys.all });
		if (enrichmentFailed) {
			toaster.error('Room enrichment', `Room enrichment failed for ${activeEnrichment.hotelName}.`);
		} else {
			toaster.success('Room enrichment', `Room enrichment completed for ${activeEnrichment.hotelName}.`);
		}
	}, [activeEnrichment, enrichmentQueries, queryClient, toaster]);

	useEffect(() => {
		setHotels(companyHotel.getAllHotelsWithMappings());
		if (!selectedHotel)
			setSelectedHotel(companyHotel.mainHotel.competitors[0]);
		else {
			const h = companyHotel.findHotelWithMappingsById(selectedHotel.hotel.id);
			setSelectedHotel(h || companyHotel.mainHotel.competitors[0]);
		}
	}, [companyHotel]);

	const sanitizeRoomMappingsMutation = useSanitizeRoomMappingsMutation();

	useEffect(() => {
		if (!!selectedHotel && selectedHotel.hasUnmappedRooms) {
			sanitizeRoomMappingsMutation.mutate({ companyHotelId: companyHotel.id, hotelId: selectedHotel.hotel.id });
		}
	}, [selectedHotel]);


	return <>

		<div className="mt-8 space-y-6">
			{!_isEmpty(hotels) && !!selectedHotel && <div>
				<div className="sm:hidden">
					<label htmlFor="tabs" className="sr-only">
						Select a tab
					</label>
					{/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
					<select
						id="tabs"
						value={selectedHotel.hotel.id}
						onChange={(e) => setSelectedHotel(companyHotel.findHotelWithMappingsById(parseInt(e.target.value))!)}
						className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
					>
						{hotels!.map((tab) => (
							<option key={tab.hotel.id} value={tab.hotel.id}>{tab.hotel.name}</option>
						))}
					</select>
				</div>
				<div className="hidden sm:block">
					<nav className="flex gap-x-4 flex-wrap gap-y-2" aria-label="Tabs">
						{hotels!.map((tab) => (
							<a
								key={tab.hotel.id}
								onClick={() => setSelectedHotel(tab)}
								className={Utilities.classNames(
									selectedHotel.hotel.id === tab.hotel.id ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-500 hover:text-gray-700',
									'rounded-md px-3 py-2 text-sm font-medium cursor-pointer',
								)}
								aria-current={selectedHotel.hotel.id === tab.hotel.id ? 'page' : undefined}
							>
								{tab.hotel.name}
								{companyHotel.countUnmappedHotelRoomsFor(tab, provider, true, false) > 0 && <span
									className={Utilities.classNames(
										selectedHotel.hotel.id === tab.hotel.id ? 'bg-red-50 text-red-700' : 'bg-red-50 text-red-700',
										'ml-2 hidden rounded-full py-0.5 px-2.5 text-xs font-medium md:inline-block',
									)}
								>{companyHotel.countUnmappedHotelRoomsFor(tab, provider, true, false)}</span>}
							</a>
						))}
					</nav>
				</div>
			</div>}
			<div className="flex justify-end gap-2">
				<FormButton state="white"
								size="small"
								onClick={() => enrichHotelRoomsMutation.mutate({
									companyHotelId: companyHotel.id,
									hotelId: selectedHotel.hotel.id,
								})}
								disabled={enrichHotelRoomsMutation.isPending || activeEnrichment !== null}
								isLoading={enrichHotelRoomsMutation.isPending || activeEnrichment !== null}>
					{activeEnrichment ? 'Enriching rooms...' : 'Enrich rooms'}
				</FormButton>
				{provider === DataProvider.BOOKING && companyHotel.settings.roomMapping.showSystemRoomMappingTypes && <FormButton state="primary"
									size="small"
									onClick={() => dialog.open()}
									disabled={automapHotelRoomsMutation.isPending}
									isLoading={automapHotelRoomsMutation.isPending}>
					Auto map this hotel
				</FormButton>}
			</div>
			<CompanyHotelRoomMappingItemFragment companyHotel={companyHotel}
																					 hotelWithRoomMappings={selectedHotel}
																					 provider={provider} />
		</div>
		<Dialogue ref={dialog.ref} title="Auto map room types" onConfirm={automapping}>
			<Message state="warning">
				<div className="space-y-2">
					<p>This action maps the Booking.com rooms only. It will overwrite eligible room types for <strong>{selectedHotel.hotel.name}</strong>. Are you sure?</p>
					<p>Rooms without size or capacity details keep their existing mappings.</p>
				</div>
			</Message>
		</Dialogue>

	</>;
};


const HotelRoomTypeConfigurationModals: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	addRoomTypeModal: Ref<ModalHandler>,
	editRoomTypeModal: Ref<ModalHandler>
}>
	= ({ companyHotel, addRoomTypeModal, editRoomTypeModal }) => {

	return <>
		<AddHotelRoomTypeModal ref={addRoomTypeModal} configs={companyHotel.roomMappingTypeConfigurations!} />
		<EditHotelRoomTypeModal ref={editRoomTypeModal} configs={companyHotel.roomMappingTypeConfigurations!} />
	</>;
};

export default RateshopperRoomMappingSettingsPage;
